import { useState, useEffect } from "react";
import { Button } from "../../../../components/ui/button";
import { ArrowLeft } from "lucide-react";
import Logo from "../../../../components/Logo/Logo";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../../components/ui/select";

import { Admin_WorkShift_LIST } from "../../../../constants/adminData";
import { WorkShift } from "../../../../types/admin";
import { useDispatch } from "react-redux";
import { updateProfessionalInfo } from "../../../../redux/slices/userProfileSlice";
import { useAppSelector } from "../../../../store";

interface WorkShiftProps {
  onBack: () => void;
  onContinue: () => void;
}

export default function WorkShiftPage({ onBack, onContinue }: WorkShiftProps) {
  const dispatch = useDispatch();
  const profile = useAppSelector((state) => state.userProfile);

  const [workShiftId, setWorkShiftId] = useState("");
  const [workShifts, setWorkShifts] = useState<WorkShift[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

useEffect(() => {
  if (!loading && workShifts.length > 0) {
    const saved = profile.professionalInfo.workShiftID;
    setWorkShiftId(saved ? String(saved) : "");
  }
}, [loading, workShifts, profile.professionalInfo.workShiftID]);

  useEffect(() => {
    const loadShifts = async () => {
      try {
        const response = await Admin_WorkShift_LIST();

        if (response?.success) {
          setWorkShifts(response.data);
        } else {
          setError("Failed to load work shifts.");
        }
      } catch {
        setError("Something went wrong");
      } finally {
        setLoading(false);
      }
    };

    loadShifts();
  }, []);

  const handleContinue = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!workShiftId) {
      alert("Please select your work shift");
      return;
    }

    await dispatch(
      updateProfessionalInfo({
        workShiftID: Number(workShiftId), 
      })
    );

    onContinue();
  };

  return (
    <div className="h-screen md:h-auto bg-[#F5F5F5] flex flex-col items-center px-6 pt-4 pb-12 relative">
      <div className="w-full max-w-xl flex flex-col items-center flex-1">

        <button onClick={onBack} className="absolute left-6 top-4 p-2 min-w-[44px] min-h-[44px] flex items-center justify-center z-10" aria-label="Go back">
          <ArrowLeft className="w-6 h-6 text-brand-primary" />
        </button>

        <div className="flex justify-center mb-0">
          <Logo />
        </div>

        <h1 className="text-[26px] md:text-3xl lg:text-4xl font-semibold tracking-wide text-center mb-8">Work Shift</h1>

        <form onSubmit={handleContinue} className="w-full max-w-xl flex flex-col flex-1">
          <div className={`rounded-lg mb-auto ${workShiftId ? "bg-gradient-to-r from-brand-primary to-brand-gray p-[2px]" : ""}`}>
            <Select value={workShiftId} onValueChange={setWorkShiftId}>
              <SelectTrigger
                className={`w-full bg-white h-12 md:h-14 text-base md:text-lg lg:text-xl px-4 text-[#2A3F5F] overflow-hidden ${
                  workShiftId ? "border-0 rounded-[6px]" : "border-2 border-gray-300 rounded-lg"
                }`}
              >
                <SelectValue placeholder="Select Shift" />
              </SelectTrigger>

              <SelectContent>
                 <div className="max-h-[220px] overflow-y-auto custom-scroll">
                {loading && <div className="px-4 py-2 text-gray-500">Loading...</div>}
                {error && <div className="px-4 py-2 text-red-500">{error}</div>}

                {!loading &&
                  !error &&
                  workShifts.map((s) => (
                    <SelectItem key={s.workShiftId} value={String(s.workShiftId)}>
                      {s.shiftName}
                    </SelectItem>
                  ))}
                  </div>
              </SelectContent>
            </Select>
          </div>

          <div className="mt-auto pt-10 pb-4 w-full">
            <Button
              type="submit"
              disabled={!workShiftId || loading}
              className="w-full bg-brand-primary text-white h-12 md:h-14 lg:h-16 text-lg md:text-xl lg:text-2xl font-medium rounded-lg hover:bg-brand-hover disabled:bg-gray-300"
            >
              Continue
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}
